import { FieldParser, ObjectShape, UnionBranchShape, ValidationWarning } from './types';
import { unknownValue } from './primitives';

/**
 * `optional` catches missing keys (raw === undefined) without warning. For a
 * present-but-drifted value, it rewrites the inner's `usedDefault` to
 * `undefined` (the actually-substituted value) and returns `undefined`.
 * Inner failure recovers to `undefined`; warnings already in the map.
 */
export const optional =
  <T>(inner: FieldParser<T>): FieldParser<T | undefined> =>
  (raw, path, warnings) => {
    if (raw === undefined) return { ok: true, value: undefined };
    const r = inner(raw, path, warnings);
    if (!r.ok) return { ok: true, value: undefined };
    const own = warnings.get(path);
    if (own !== undefined) {
      warnings.set(path, { ...own, usedDefault: undefined });
      return { ok: true, value: undefined };
    }
    return r;
  };

/**
 * `nullable` treats a wire `null` as the intended value (no warning) and a
 * missing key as drift (warning, value defaults to `null`). Own-level drift
 * in `inner` is rewritten so `usedDefault` reflects the substituted `null`
 * rather than the inner primitive's default; inner failure recovers to
 * `null` with warnings preserved.
 */
export const nullable =
  <T>(inner: FieldParser<T>): FieldParser<T | null> =>
  (raw, path, warnings) => {
    if (raw === null) return { ok: true, value: null };
    if (raw === undefined) {
      warnings.set(path, {
        path,
        expected: 'nullable value (key present, value null or T)',
        received: undefined,
        usedDefault: null,
      });
      return { ok: true, value: null };
    }
    const r = inner(raw, path, warnings);
    if (!r.ok) return { ok: true, value: null };
    const own = warnings.get(path);
    if (own !== undefined) {
      warnings.set(path, { ...own, usedDefault: null });
      return { ok: true, value: null };
    }
    return r;
  };

/**
 * Structural-only: returns `ok: false` on a non-array wire (with a warning
 * at this position). Per-item parsing; items that return `ok: false` are
 * dropped (their warnings remain in the map). Defaulted items are kept —
 * use `enforce` to declaratively drop bad rows.
 */
export const array =
  <T>(item: FieldParser<T>): FieldParser<T[]> =>
  (raw, path, warnings) => {
    if (!Array.isArray(raw)) {
      warnings.set(path, {
        path,
        expected: 'array',
        received: raw,
        usedDefault: undefined,
      });
      return { ok: false };
    }
    const value: T[] = [];
    raw.forEach((it, i) => {
      const r = item(it, `${path}[${i}]`, warnings);
      if (r.ok) value.push(r.value);
      // ok:false items are dropped; their warnings are already in the map.
    });
    return { ok: true, value };
  };

/**
 * Structural-only: returns `ok: false` on a non-object/null/array wire (with
 * a warning at this position). Iterates fields; inner `ok: false` propagates
 * so failures at deeper levels still bubble up. Extra fields on the wire are
 * silently ignored.
 */
export const object =
  <T extends object>(shape: ObjectShape<T>): FieldParser<T> =>
  (raw, path, warnings) => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      warnings.set(path, {
        path,
        expected: 'object',
        received: raw,
        usedDefault: undefined,
      });
      return { ok: false };
    }
    const source = raw as Record<keyof T, unknown>;
    // Mutable scratch: `-readonly` strips readonly so we can assign into it
    // (the result `T` may have readonly props), `?` makes `{}` a valid start,
    // and `T[P]` keeps the per-field assignment type-checked. The boundary
    // cast below bridges scratch -> T.
    const value: { -readonly [P in keyof T]?: T[P] } = {};
    for (const key of Object.keys(shape) as Array<keyof T>) {
      const fieldParser = shape[key];
      const subPath = path === '' ? String(key) : `${path}.${String(key)}`;
      const r = fieldParser(source[key], subPath, warnings);
      if (!r.ok) return { ok: false };
      value[key] = r.value;
    }
    return { ok: true, value: value as T };
  };

const describeForNote = (v: unknown): string => {
  if (typeof v === 'string') return JSON.stringify(v);
  if (v === null) return 'null';
  if (v === undefined) return 'undefined';
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return typeof v;
};

/**
 * Maps a successfully-parsed `T` to a `U`. `fn` returns `undefined` to signal
 * "can't map this", which becomes `ok: false`. On that path, if `inner`
 * already drifted at the same path, the transform's failure is appended to
 * the existing warning's `notes` (the inner's drift is the root cause); if
 * `inner` was clean, a fresh warning is written.
 *
 * Apply `optional`/`nullable` *outside* a transform (e.g.
 * `optional(pureTransform(...))`): `U` should be a concrete type, with
 * missing/null handled by the wrapping combinator.
 */
export const pureTransform =
  <T, U>(inner: FieldParser<T>, fn: (value: T) => U | undefined): FieldParser<U> =>
  (raw, path, warnings) => {
    const r = inner(raw, path, warnings);
    if (!r.ok) return { ok: false };
    const mapped = fn(r.value);
    if (mapped === undefined) {
      const note = `transform could not map ${describeForNote(r.value)}`;
      const own = warnings.get(path);
      if (own !== undefined) {
        warnings.set(path, {
          ...own,
          notes: [...(own.notes ?? []), note],
        });
      } else {
        warnings.set(path, {
          path,
          expected: 'a value the transform can map',
          received: r.value,
          usedDefault: undefined,
          notes: [note],
        });
      }
      return { ok: false };
    }
    return { ok: true, value: mapped };
  };

/**
 * Recovers a failure (`ok: false`) to `fallback`, keeping the warnings that
 * explain why it failed. Also rewrites the path's `usedDefault` to
 * `fallback`, so the warning reflects the value actually substituted (rather
 * than an inner primitive's earlier default).
 *
 * Catches failures, not defaults: `withDefault(str(), 'x')` is a no-op
 * because `str` never fails (it defaults to `''`); `withDefault` only bites
 * on failure-producing inners like `required`, `pureTransform`, `array`, and
 * `object`.
 */
export const withDefault =
  <T>(inner: FieldParser<T>, fallback: T): FieldParser<T> =>
  (raw, path, warnings) => {
    const r = inner(raw, path, warnings);
    if (r.ok) return r;
    const own = warnings.get(path);
    if (own !== undefined) {
      warnings.set(path, { ...own, usedDefault: fallback });
    }
    return { ok: true, value: fallback };
  };

/**
 * Ergonomic composition of `pureTransform` + `withDefault`: map `T` to `U`,
 * falling back to `fallback` (with a warning) if either the mapping or the
 * inner parse fails.
 */
export const transform = <T, U>(inner: FieldParser<T>, fn: (value: T) => U | undefined, fallback: U): FieldParser<U> =>
  withDefault(pureTransform(inner, fn), fallback);

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return Number.isNaN(value) ? undefined : value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
};

/**
 * Accepts a number or a numeric string and normalizes to `number`, defaulting
 * to `fallback` (with a warning) on anything else. Postgres serializes
 * `bigint`/`numeric`/`COUNT(*)` as strings, so list endpoints routinely send
 * counts and ids as numeric strings. Use `required(numeric())` to reject a
 * non-numeric value instead of defaulting.
 */
export const numeric = (fallback = 0): FieldParser<number> => transform(unknownValue(), toNumber, fallback);

/**
 * Discriminated-union parser. `tag` names a string-typed field on `T` whose
 * literal value selects the branch; `branches` maps each literal to an
 * `ObjectShape` describing the non-discriminator fields of that branch
 * (the discriminator is omitted via `Omit` — its value is asserted by this
 * parser at runtime and merged back into the result).
 *
 * Returns `ok: false` when (a) `raw` is not an object, (b) the discriminator
 * is missing or non-string, or (c) the discriminator value names no branch.
 * Inside an `array(...)` that failure drops the row; inside an `object(...)`
 * it propagates so the enclosing record fails too. There is deliberately no
 * default branch — a record whose discriminator the frontend doesn't know
 * is uninterpretable, and inventing a fallback would silently mis-route the
 * other fields.
 */
export const union = <T extends Record<K, string>, K extends keyof T & string>(
  tag: K,
  branches: { [V in T[K]]: UnionBranchShape<T, K, V> }
): FieldParser<T> => {
  const branchParsers: Record<string, FieldParser<object>> = {};
  for (const key of Object.keys(branches)) {
    branchParsers[key] = object((branches as Record<string, ObjectShape<object>>)[key]);
  }
  const branchKeys = Object.keys(branches);
  return (raw, path, warnings) => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      warnings.set(path, {
        path,
        expected: `discriminated union on ${tag}`,
        received: raw,
        usedDefault: undefined,
      });
      return { ok: false };
    }
    const tagValue = (raw as Record<string, unknown>)[tag];
    const tagPath = path === '' ? tag : `${path}.${tag}`;
    if (typeof tagValue !== 'string' || !Object.prototype.hasOwnProperty.call(branchParsers, tagValue)) {
      warnings.set(tagPath, {
        path: tagPath,
        expected: `one of: ${branchKeys.join(', ')}`,
        received: tagValue,
        usedDefault: undefined,
      });
      return { ok: false };
    }
    const branchResult = branchParsers[tagValue](raw, path, warnings);
    if (!branchResult.ok) return { ok: false };
    return {
      ok: true,
      value: { ...branchResult.value, [tag]: tagValue } as T,
    };
  };
};
